import React, {useContext, useEffect, useReducer, useRef, useState, useCallback, useMemo} from "react";
import useTooltip from "@hooks/useTooltip";
import useUrlHash from "@hooks/useUrlHash";
import {ContentContext} from "@context/ContentContext";
import {BrandContext} from "@context/BrandContext";
import axios from "@common/axios";
import TabAccessibilityWrap from "../nav/TabAccessibilityWrap";
import CircularLoader from "@common/CircularLoader";
import DynamicContent from "../nav/DynamicContent";
import AlgebraKitBlock from "@common/AlgebraKitBlock";
import {UserContext} from "@context/UserContext";
import GenericDialog from "@common/GenericDialog";
import NavigationRightDevelopment from "../nav/NavigationRightDevelopment";
import PreviousNextLessonDevelopment from "../nav/PreviousNextLessonDevelopment";
import LessonButtons from "./LessonButtons";
import WalkthroughsAndQuestions from "./WalkthroughsAndQuestions";
import PracticeQuestions from "../questions/PracticeQuestions";
import Questions from "../questions/explainQuestions/Questions";
import LessonScorcher from "./LessonScorcher";
import Walkthroughs from "../walkthroughs/Walkthroughs";
import BookmarkLink from "../bookmarks/BookmarkLink";
import {BookmarkAddIcon} from "@common/icons";
import {useScreenMode} from "@common/context/ScreenModeContext";
import Alert from "@common/Alert";
import LessonResources from "./LessonResources";

const TabButtons = React.memo(({tabs, activeTab, smallScreenMode, hideSingleTabTitle, tabRefs, handleChangeTab}) => {
    if (tabs.length <= 1) return '';
    return (
        <>
            {tabs.map((tab, index) => {
                const isActive = activeTab && tab.tabId === activeTab.tabId;
                const tabId = `lesson-tab-${tab.tabId}`;
                return (
                    <button type="button"
                            className={"js-gen-tab ch-nav-item " + (isActive ? " sel" : "")}
                            ref={(el) => tabRefs.current.set(tab.tabId, el)}
                            key={tab.tabId}
                            id={tabId}
                            tabIndex={isActive ? 0 : -1}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls="lesson-tabpanel"
                            data-hash={tab.hashName}
                            data-tab-id={tab.tabId}
                            data-tab-brand-id={tab.tabBrandId}
                            onClick={() => handleChangeTab(tab.hashName)}
                            dangerouslySetInnerHTML={{__html: tab.tabName}}></button>
                )
            })}
        </>
    )
});

const ACTIONS = {
    HYDRATE_STATE: "hydrateState",
    UPDATE_ACTIVE_TAB: "updateActiveTab",
    UPDATE_ACTIVE_HASH: "updateActiveHash",
    UPDATE_TAB_LOADED: "updateTabLoaded"
}

const defaultState = {
    singlePanel: true,
    tabs: [],
    tabLoading: true,
    tabsFetched: false,
    staticTab: null,
    activeTab: null,
    activeTabSmallScreen: null,
    hash: {},
}

const reducer = (state, action) => {
    if (action.type === ACTIONS.HYDRATE_STATE) {
        return {
            ...defaultState,
            singlePanel: action.payload.singlePanel,
            tabs: action.payload.tabs,
            staticTab: action.payload.staticTab,
            tabsFetched: true,
        }
    } else if (action.type === ACTIONS.UPDATE_ACTIVE_HASH) {
        return {
            ...state,
            hash: action.payload.hash,
        };
    } else if (action.type === ACTIONS.UPDATE_ACTIVE_TAB) {
        return {
            ...state,
            tabLoading: true,
            hash: action.payload.hash,
            activeTab: action.payload.activeTab.layoutAttribute === "LEFT_COLUMN" ? state.activeTab : action.payload.activeTab,
            activeTabSmallScreen: action.payload.activeTab,
        }
    } else if (action.type === ACTIONS.UPDATE_TAB_LOADED) {
        return {
            ...state,
            tabLoading: action.payload.loading
        }
    } else {
        return state;
    }
}

const tabComponents = {
    AlgebraKitBlock: AlgebraKitBlock,
    DynamicContent: DynamicContent,
    PracticeQuestions: PracticeQuestions,
    Questions: Questions,
    Walkthroughs: Walkthroughs,
    WalkthroughsAndQuestions: WalkthroughsAndQuestions,
    LessonScorcher: LessonScorcher,
    LessonResources: LessonResources
}

const Lesson = props => {
    const {smallScreenMode} = useScreenMode();
    const {content, updateCssStylesAndClasses} = useContext(ContentContext);
    const brandConfig = useContext(BrandContext);
    const {user} = useContext(UserContext);
    const [bookmarkDialogOpen, setBookmarkDialogOpen] = useState(false);
    const tabRefs = useRef(new Map());
    const userActivatedTab = useRef(false);
    const contentRef = useRef(content);

    const {hashSegments, updateHash, findTabByHash} = useUrlHash();
    const [state, dispatch] = useReducer(reducer, defaultState);

    useEffect(() => {
        contentRef.current = content;
    }, [content]);

    useTooltip(".ch-lesson-width");

    const effectiveHashSegments = useMemo(() => {
        if (hashSegments && hashSegments[0] === 'scorcher' && hashSegments.length === 1) {
            return ['scorcher', 'world'];
        }
        return hashSegments;
    }, [hashSegments]);

    useEffect(() => {
        if (hashSegments && hashSegments.length === 1 && hashSegments[0] === 'scorcher') {
            const newUrl = window.location.pathname + window.location.search + '#/scorcher/world';
            window.history.replaceState(null, '', newUrl);
        }
    }, [hashSegments]);

    const buildHashObject = useCallback((hash, tab) => {
        return {
            urlHash: hash.join('/'),
            tabHash: tab ? tab.hash : undefined,
            params: tab && tab.hash ? hash.slice(tab.hash.length) : []
        };
    }, []);

    const configureTab = useCallback((tab) => {
        if (!tab) return null;

        const params = tab.hash ? effectiveHashSegments.slice(tab.hash.length) : [];

        let compiledUrl = tab.url;
        if (compiledUrl) {
            compiledUrl = compiledUrl.replace(/\{#(\d+)\}/g, (match, number) => {
                const index = parseInt(number, 10) - 1;
                return (params[index] !== undefined) ? params[index] : '';
            });
        }

        const contentCallback = () => {
            if (tab.tabId != 14 && tab.loadFunction && typeof window[tab.loadFunction] === "function") {
                try {
                    window[tab.loadFunction](params, () => {
                        if (typeof buildPageMarkers === 'function') buildPageMarkers();
                        dispatch({type: ACTIONS.UPDATE_TAB_LOADED, payload: {loading: false}});
                    });
                } catch (e) {
                    console.error('Error running lesson scripts', e);
                    dispatch({type: ACTIONS.UPDATE_TAB_LOADED, payload: {loading: false}});
                }
            } else {
                dispatch({type: ACTIONS.UPDATE_TAB_LOADED, payload: {loading: false}});
            }
            updateCssStylesAndClasses(contentRef.current);
        };

        return {...tab, compiledUrl, contentCallback};
    }, [effectiveHashSegments, updateCssStylesAndClasses]);

    const handleChangeTab = useCallback(tabHash => {
        userActivatedTab.current = true;
        updateHash(tabHash);
    }, [updateHash]);

    const singlePanel = !brandConfig.twoColumnLayout && !user.supplementary;

    useEffect(() => {
        const abortController = new AbortController();
        const fetchTabs = async () => {
            if (!content.currentConcept) return;
            try {
                const response = await axios.get("/web/ui/tab/all/me", {
                    signal: abortController.signal
                });

                const tabs = response.data.map(tab => ({
                    ...tab,
                    hash: tab.hashName
                        ? tab.hashName.split('.').filter(seg => seg !== "#" && seg !== "")
                        : null
                }));
                const staticTab = tabs.find(tab => tab.layoutAttribute === "LEFT_COLUMN");
                const configuredStaticTab = configureTab(staticTab);
                dispatch({
                    type: ACTIONS.HYDRATE_STATE,
                    payload: {singlePanel, tabs, staticTab: configuredStaticTab}
                });
            } catch(error) {
                if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
                    return;
                }
                console.error("Error loading tabs", error);
            }
        };
        fetchTabs();

        return () => abortController.abort();
    }, [content.currentConcept?.conceptId]);

    const visibleTabs = useMemo(() => {
        return state.tabs.filter(tab =>
            tab.hasContent &&
            !tab.childTab &&
            !tab.hidden &&
            (tab.layoutAttribute !== "LEFT_COLUMN" || smallScreenMode) &&
            (!tab.singleTab || !brandConfig.hideSingleTabTitle)
        );
    }, [state.tabs, smallScreenMode, brandConfig.hideSingleTabTitle]);

    useEffect(() => {
        if (!visibleTabs || visibleTabs.length === 0) return;

        const newActiveTab = findTabByHash(visibleTabs, effectiveHashSegments);

        if (!newActiveTab) {
            const currentActiveTab = smallScreenMode ? state.activeTabSmallScreen : state.activeTab;
            if ((!effectiveHashSegments || effectiveHashSegments.length === 0) && currentActiveTab) {
                return;
            }

            let defaultTab = visibleTabs.find(tab => tab.defaultTab);
            if (!defaultTab) defaultTab = visibleTabs[0];
            if (defaultTab) updateHash(defaultTab.hashName);
        } else {
            const hashObject = buildHashObject(effectiveHashSegments, newActiveTab);
            const configured = configureTab(newActiveTab);
            const currentActiveTab = smallScreenMode ? state.activeTabSmallScreen : state.activeTab;

            const isDifferentTab = newActiveTab.tabId !== currentActiveTab?.tabId;
            const isDifferentUrl = configured?.compiledUrl !== currentActiveTab?.compiledUrl;
            const isDifferentHash = hashObject.urlHash !== state.hash?.urlHash;

            if (isDifferentTab || isDifferentUrl) {
                dispatch({ type: ACTIONS.UPDATE_ACTIVE_TAB, payload: { activeTab: configured, hash: hashObject } });
            } else if (isDifferentHash) {
                dispatch({ type: ACTIONS.UPDATE_ACTIVE_HASH, payload: { hash: hashObject } });
            }
        }

    }, [visibleTabs, effectiveHashSegments, smallScreenMode]);

    useEffect(() => {
        if (state.tabsFetched && state.tabLoading && visibleTabs.length === 0) {
            dispatch({ type: ACTIONS.UPDATE_TAB_LOADED, payload: { loading: false } });
        }
    }, [state.tabsFetched, state.tabLoading, visibleTabs]);

    useEffect(() => {
        if (userActivatedTab.current && state.activeTab && !state.tabLoading) {
            const tabRef = tabRefs.current.get(state.activeTab.tabId);
            if (tabRef) {
                tabRef.focus();
                userActivatedTab.current = false;
            }
        }
    }, [state.activeTab, state.tabLoading]);

    const activeTab = smallScreenMode ? state.activeTabSmallScreen : state.activeTab;
    const ContentComponent = activeTab ? tabComponents[activeTab.tabComponent]: undefined;
    const StaticComponent = state?.staticTab ? tabComponents[state.staticTab.tabComponent]: undefined;

    if (!content.currentConcept) {
        return <CircularLoader visible={true} />;
    }

    return (
        <div key={`lesson-${content.currentConcept.conceptId}`}>
            {state.tabs && state.tabs.length > 0 && (
                <div className="ch-tab-bar js-lesson-tab-bar">
                    <div className="ch-tab-scroller">
                        {!singlePanel && !smallScreenMode ? (
                            <div className="ch-tab-scroller-area ch-tab-scroller-two-panel">
                                <div className="ch-tab-scroller-content ch-lesson-content-two-panel">
                                    <LessonButtons activeTab={activeTab} setBookmarkDialogOpen={setBookmarkDialogOpen} singlePanel={false} />
                                </div>
                                <TabAccessibilityWrap aria-label="Lesson Navigation" className="ch-lesson-content-two-panel" orientation="horizontal">
                                    <div role="tablist" aria-label="lesson tabs" aria-orientation="horizontal" className="ch-tab-scroller-content ch-nav-group">
                                        <TabButtons
                                            tabs={visibleTabs}
                                            activeTab={activeTab}
                                            smallScreenMode={smallScreenMode}
                                            hideSingleTabTitle={brandConfig.hideSingleTabTitle}
                                            tabRefs={tabRefs}
                                            handleChangeTab={handleChangeTab}
                                        />
                                    </div>
                                </TabAccessibilityWrap>
                            </div>
                        ) : (
                            <div className="ch-tab-scroller-area">
                                <TabAccessibilityWrap aria-label="Lesson Navigation" orientation="horizontal">
                                    <div role="tablist" aria-label="lesson tabs" aria-orientation="horizontal" className="ch-tab-scroller-content ch-nav-group">
                                        <TabButtons
                                            tabs={visibleTabs}
                                            activeTab={activeTab}
                                            smallScreenMode={smallScreenMode}
                                            hideSingleTabTitle={brandConfig.hideSingleTabTitle}
                                            tabRefs={tabRefs}
                                            handleChangeTab={handleChangeTab}
                                        />
                                    </div>
                                </TabAccessibilityWrap>
                                <LessonButtons activeTab={activeTab} setBookmarkDialogOpen={setBookmarkDialogOpen} singlePanel={true} smallScreenMode={smallScreenMode} />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div role="tabpanel"
                 id="lesson-tabpanel"
                 aria-labelledby={activeTab ? `lesson-tab-${activeTab.tabId}` : undefined}
                 className={'ch-lesson-wrapper' + (brandConfig.useDevelopmentNavigation && brandConfig.centerLessonContent ? ' ch-lesson-center-content' : '')} style={{width: "100%"}}>
                <div className={'ch-lesson-width' + (brandConfig.useContentTemplate ? ' ch-lesson-width-template' : ' ch-lesson-width-non-template')}>
                    <div className="lesson-content js-lessonContent flex" data-concept-id={content && content.currentConcept ? content.currentConcept.conceptId: ''}>
                        {!singlePanel && !smallScreenMode && (
                            <div className="ch-lesson-content-two-panel flex margin-r--m">
                                <CircularLoader visible={!state?.staticTab || !StaticComponent || state.tabLoading}/>
                                {state?.staticTab && StaticComponent && (
                                    <StaticComponent
                                        url={state.staticTab.compiledUrl}
                                        className={state.staticTab.tabClass ? state.staticTab.tabClass : ''}
                                        style={{width: '100%'}}
                                        entityId={content.currentConcept.conceptId}
                                        updateHash={false}
                                        callback={state.staticTab.contentCallback}
                                    />
                                )}
                            </div>
                        )}
                        <div className={"ch-lesson ch-lesson-content" + (brandConfig.useContentTemplate ? ' ch-lesson-template' : ' ch-lesson-non-template') + (!singlePanel && !smallScreenMode ? " ch-lesson-content-two-panel" : "")}>
                            <CircularLoader visible={state.tabLoading} displayInElement={true}/>
                            {state.tabsFetched && !state.tabLoading && visibleTabs.length === 0 ? (
                                <Alert type="warning" title="No content is available for this section." />
                            ) : (
                                <div id="generalContent" className={'ch-lesson-content-body' + (state.tabLoading ? ' hide' : '')}>
                                    <>
                                        {brandConfig.showContentTitle && activeTab && activeTab.contentTitle && (
                                            <div className="text-heading-unit">
                                                {content.currentConcept.conceptName}
                                            </div>
                                        )}
                                        {brandConfig.showConceptName && activeTab && activeTab.showConceptName && content && content.currentConcept && content.currentConcept.conceptName &&
                                            <div className="cont-base">
                                                <span className="cont-head-a">{content.currentConcept.conceptName}</span>
                                            </div>
                                        }
                                        {activeTab && ContentComponent &&
                                            <ContentComponent
                                                conceptId={content.currentConcept.conceptId}
                                                url={activeTab.compiledUrl}
                                                hashName={activeTab.hashName}
                                                hash={state.hash}
                                                updateHash={false}
                                                className={activeTab.tabClass ? activeTab.tabClass : ''}
                                                style={{width: '100%'}}
                                                callback={activeTab.contentCallback}
                                            />
                                        }
                                    </>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {
                    brandConfig.useDevelopmentNavigation && !smallScreenMode && !state.tabLoading && state.tabs.length > 0 &&
                    <NavigationRightDevelopment smallScreenMode={smallScreenMode} compressed={false} callback={null} />
                }
            </div>
            {brandConfig.useDevelopmentNavigation && !state.tabLoading && state.tabs.length > 0 &&
                <PreviousNextLessonDevelopment />
            }
            {state?.activeTab &&
                <GenericDialog
                    title="Add Bookmark"
                    icon={<BookmarkAddIcon fill={'#ffffff'} />}
                    open={bookmarkDialogOpen}
                    onClose={() => setBookmarkDialogOpen(false)}
                    fullWidth
                    buttons={[
                        {
                            name: 'Close',
                            callback: async () => setBookmarkDialogOpen(false)
                        }
                    ]}
                >
                    <BookmarkLink conceptId={content.currentConcept.conceptId} urlHash={hashSegments} tabBrandId={state.activeTab.tabBrandId} resourceType={state.activeTab.resourceType}/>
                </GenericDialog>
            }
        </div>
    )
}

export default Lesson;